import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Loads a binary resource (font or image) from a URL and returns a base64 string.
 */
const fetchAsBase64 = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
  }
  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const CHUNK = 8192;
  for (let i = 0; i < bytes.byteLength; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
};

/**
 * Loads the app logo from /logo192.png and returns a data URL.
 */
const fetchLogoDataUrl = async () => {
  try {
    const logoUrl = `${window.location.origin}/logo192.png`;
    const response = await fetch(logoUrl);
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

// Font sources — local-only. Place fonts in `public/fonts/`:
// - public/fonts/NotoSans-Regular.ttf
// - public/fonts/NotoSans-Bold.ttf (optional)
// - public/fonts/NotoSans-Italic.ttf (optional)
// - public/fonts/YatraOne-Regular.ttf (optional)

const loadFonts = async (doc) => {
  const tryUrls = async (urls) => {
    for (const u of urls) {
      try {
        const response = await fetch(u);
        if (!response.ok) {
          continue;
        }
        const buffer = await response.arrayBuffer();
        if (!buffer || buffer.byteLength === 0) {
          continue;
        }
        const bytes = new Uint8Array(buffer);
        const sig = String.fromCharCode(bytes[0] || 0, bytes[1] || 0, bytes[2] || 0, bytes[3] || 0);
        const isTtf = (bytes[0] === 0 && bytes[1] === 1 && bytes[2] === 0 && bytes[3] === 0) || sig === 'OTTO' || sig === 'ttcf';
        if (!isTtf) {
          console.warn(`Resource at ${u} does not appear to be TTF/OTF (sig='${sig}'). Skipping.`);
          continue;
        }
        let binary = '';
        const CHUNK = 8192;
        for (let i = 0; i < bytes.byteLength; i += CHUNK) {
          binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
        }
        const b64 = btoa(binary);
        return b64;
      } catch (_err) {
        continue;
      }
    }
    throw new Error('All fetch attempts failed');
  };

  try {
    const localReg = `${window.location.origin}/fonts/NotoSans-Regular.ttf`;
    const localBold = `${window.location.origin}/fonts/NotoSans-Bold.ttf`;
    const localItalic = `${window.location.origin}/fonts/NotoSans-Italic.ttf`;
    const localYatra = `${window.location.origin}/fonts/YatraOne-Regular.ttf`;

    let notoReg;
    try {
      notoReg = await tryUrls([localReg]);
    } catch (e) {
      console.warn('Local NotoSans-Regular.ttf not found. Run `node scripts/download-fonts.js`.');
      throw e;
    }

    let notoBold = null;
    let notoItalic = null;
    try {
      notoBold = await tryUrls([localBold]);
    } catch {
      notoBold = null;
    }
    try {
      notoItalic = await tryUrls([localItalic]);
    } catch {
      notoItalic = null;
    }

    let yatraOne = null;
    try {
      yatraOne = await tryUrls([localYatra]);
    } catch {
      yatraOne = null;
    }

    try {
      // Register regular
      doc.addFileToVFS('NotoSans-Regular.ttf', notoReg);
      doc.addFont('NotoSans-Regular.ttf', 'NotoSans', 'normal');
      try {
        doc.addFont('NotoSans-Regular.ttf', 'NotoSans-Regular', 'normal');
      } catch (e) {
        // non-fatal
      }

      if (notoBold) {
        doc.addFileToVFS('NotoSans-Bold.ttf', notoBold);
        doc.addFont('NotoSans-Bold.ttf', 'NotoSans', 'bold');
        try { doc.addFont('NotoSans-Bold.ttf', 'NotoSans-Regular', 'bold'); } catch (e) {}
      } else {
        try {
          doc.addFileToVFS('NotoSans-Bold.ttf', notoReg);
        } catch (e) {}
        try {
          doc.addFont('NotoSans-Bold.ttf', 'NotoSans', 'bold');
          doc.addFont('NotoSans-Bold.ttf', 'NotoSans-Regular', 'bold');
        } catch (e) {
          console.warn('Failed to register NotoSans bold fallback:', e);
        }
      }

      if (notoItalic) {
        try {
          doc.addFileToVFS('NotoSans-Italic.ttf', notoItalic);
          doc.addFont('NotoSans-Italic.ttf', 'NotoSans', 'italic');
          doc.addFont('NotoSans-Italic.ttf', 'NotoSans-Regular', 'italic');
        } catch (e) {
          console.warn('Failed to add NotoSans-Italic to jsPDF VFS:', e);
        }
      }

      if (yatraOne) {
        try {
          doc.addFileToVFS('YatraOne-Regular.ttf', yatraOne);
          doc.addFont('YatraOne-Regular.ttf', 'YatraOne', 'normal');
        } catch (err) {
          console.warn('Failed to add YatraOne to jsPDF VFS:', err);
        }
      }

      try {
        doc.getFontList();
      } catch (e) {
        // ignore
      }
    } catch (err) {
      console.warn('Font loading failed:', err);
      throw err;
    }

    return { heading: yatraOne ? 'YatraOne' : 'NotoSans', body: 'NotoSans' };
  } catch (err) {
    console.warn('Font loading failed (offline):', err);
    return { heading: 'helvetica', body: 'helvetica' };
  }
};

/**
 * Generates and returns a jsPDF document with a formatted account statement.
 * Always generates in English regardless of user's selected language.
 */
export const generateStatementPdf = async (
  account,
  transactions,
  tenantBrand,
  statementPeriod,
  customFromDate,
  customToDate,
  locale,
  t
) => {
  const pdfLocale = 'en-IN';
  
  const enStrings = {
    'account.accountStatement': 'ACCOUNT STATEMENT',
    'common.period': 'Period',
    'common.generatedAt': 'Generated on',
    'common.accountInformation': 'Account Details',
    'common.accountNumber': 'Account Number',
    'common.accountName': 'Account Name',
    'common.accountType': 'Account Type',
    'common.availableBalance': 'Balance',
    'account.roi': 'ROI',
    'account.loanTenure': 'Tenure',
    'account.emiAmount': 'EMI',
    'account.overdue': 'Overdue',
    'common.date': 'Date',
    'common.remarks': 'Narration',
    'common.debit': 'Debit',
    'common.credit': 'Credit',
    'common.balance': 'Balance',
    'common.page': 'Page',
    'common.computerGenerated': 'Computer generated statement — signature not required.',
    'account.last3Months': 'Last 3 Months',
    'account.last6Months': 'Last 6 Months',
    'account.currentFy': 'Current Fin Year',
    'account.previousFy': 'Previous Fin Year',
  };

  const englishT = (key, params = {}, fallback = '') => enStrings[key] || fallback || key;
  const doc = new jsPDF();

  doc.setProperties({
    title: `Statement - ${account.acno || 'Account'}`,
    subject: 'Account Statement',
    author: tenantBrand.fullName,
    creator: tenantBrand.fullName,
  });

  const [fonts, logoDataUrl] = await Promise.all([
    loadFonts(doc),
    fetchLogoDataUrl(),
  ]);

  const headingFont = fonts.heading || fonts.body || 'helvetica';
  const brandName = String(tenantBrand?.fullName || tenantBrand?.shortName || 'Account');

  let rupeeSymbol = 'Rs.';
  try {
    if (fonts && fonts.body && String(fonts.body).toLowerCase() !== 'helvetica') {
      rupeeSymbol = '₹';
    }
  } catch {
    rupeeSymbol = 'Rs.';
  }

  const primaryColor = [224, 79, 13];
  const textColor    = [31,  31,  31];
  const grayColor    = [100, 100, 100];
  const ruleColor    = [210, 210, 210];

  const fmtCurrency = (amount) => {
    const numeric = Number(amount);
    if (Number.isNaN(numeric)) return '-';
    const sign = numeric < 0 ? '-' : '';
    const absValue = Math.abs(numeric);
    const [whole, fraction = '00'] = absValue.toFixed(2).split('.');
    const groupedWhole = (() => {
      if (whole.length <= 3) return whole;
      const lastThree = whole.slice(-3);
      const otherDigits = whole.slice(0, -3);
      return `${otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${lastThree}`;
    })();
    return `${rupeeSymbol} ${sign}${groupedWhole}.${fraction}`;
  };

  const humanizeLabel = (rawKey) => {
    if (!rawKey) return '-';
    return String(rawKey).trim().replace(/[_\-]+/g, ' ').replace(/\s+/g, ' ').replace(/\b([a-z])/g, (m) => m.toUpperCase());
  };

  const shouldFormatAsCurrency = (key, value) => {
    if (value === null || value === undefined) return false;
    const normalizedKey = String(key || '').toLowerCase();
    const numericValue = Number(String(value).replace(/,/g, '').trim());
    if (Number.isNaN(numericValue)) return false;
    // Avoid formatting account identifiers, even if they contain loan labels.
    if (/\b(account|account number|acno|ac_no|a_c_no|ac number|a\/c|loan a\/c|loan account)\b/.test(normalizedKey)) {
      return false;
    }
    return /amount|balance|due|overdue|closing|outstanding|price|principal|penality/i.test(normalizedKey);
  };

  const fmtAccountValue = (key, value) => {
    if (value === null || value === undefined || String(value).trim() === '') return '-';
    if (shouldFormatAsCurrency(key, value)) return fmtCurrency(Number(String(value).replace(/,/g, '').trim()));
    return String(value);
  };

  const fmtDate = (dateStr) => {
    if (!dateStr || dateStr === '-') return '-';
    const parsed = new Date(dateStr);
    if (Number.isNaN(parsed.getTime())) return String(dateStr);
    return new Intl.DateTimeFormat(pdfLocale, { day: '2-digit', month: 'short', year: 'numeric' }).format(parsed);
  };

  // HEADER
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, 7, 297, 'F');

  let nameStartX = 16;
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, 'PNG', 14, 14, 16, 16);
      nameStartX = 34;
    } catch {}
  }

  doc.setFont(headingFont, 'normal');
  doc.setFontSize(17);
  doc.setTextColor(...primaryColor);
  doc.text(nameStartX, 21, brandName);

  doc.setFont(fonts.body, 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...textColor);
  doc.text(nameStartX, 28, englishT('account.accountStatement'));

  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...grayColor);

  let rangeLabel = '';
  if (statementPeriod === 'custom') {
    rangeLabel = `${fmtDate(customFromDate)} – ${fmtDate(customToDate)}`;
  } else {
    const periodKeyMap = {
      'last_3months': 'account.last3Months',
      'last_6months': 'account.last6Months',
      'current_fy': 'account.currentFy',
      'previous_fy': 'account.previousFy',
    };
    rangeLabel = englishT(periodKeyMap[statementPeriod] || `account.${statementPeriod}`, {}, '');
    if (!rangeLabel) rangeLabel = statementPeriod.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  doc.text(nameStartX, 34, `${englishT('common.period')}: ${rangeLabel}`);
  doc.text(196 - doc.getTextWidth(`${englishT('common.generatedAt')}: ${fmtDate(new Date())}`), 34, `${englishT('common.generatedAt')}: ${fmtDate(new Date())}`);

  doc.setDrawColor(...ruleColor);
  doc.setLineWidth(0.4);
  doc.line(14, 38, 196, 38);

  // ACCOUNT DETAILS
  doc.setFont(fonts.body, 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(...textColor);
  doc.text(14, 45, englishT('common.accountInformation'));

  const detailsList = Array.isArray(account.detailRows) && account.detailRows.length > 0
    ? account.detailRows.map((item) => ({ label: item.key ? String(item.key).trim() : humanizeLabel(item.key), value: fmtAccountValue(item.key, item.value) }))
    : (() => {
        const balNum = account.balance === null || account.balance === undefined ? NaN : Number(String(account.balance).replace(/,/g, '').trim());
        const balanceValue = (account.balance === null || account.balance === undefined || Number.isNaN(balNum)) ? '-' : `${fmtCurrency(balNum)} ${balNum < 0 ? '(Dr)' : '(Cr)'}`;
        return [
          { label: englishT('common.accountNumber'), value: account.acno || '-' },
          { label: englishT('common.accountName'), value: account.accountName || '-' },
          { label: englishT('common.accountType'), value: account.displayName || account.acType || '-' },
          { label: englishT('common.availableBalance'), value: balanceValue },
        ];
      })();

  const detailRowHeights = [];
  for (let rowIndex = 0; rowIndex < Math.ceil(detailsList.length / 2); rowIndex += 1) {
    const leftItem = detailsList[rowIndex * 2];
    const rightItem = detailsList[rowIndex * 2 + 1];
    const leftLines = leftItem ? doc.splitTextToSize(String(leftItem.value), 76) : [''];
    const rightLines = rightItem ? doc.splitTextToSize(String(rightItem.value), 76) : [''];
    const leftHeight = Math.max(6, leftLines.length * 4.5);
    const rightHeight = Math.max(6, rightLines.length * 4.5);
    detailRowHeights.push(Math.max(leftHeight, rightHeight));
  }
  const detailsSectionHeight = Math.max(22, detailRowHeights.reduce((acc, h) => acc + h, 0) + 10);

  // Subtle background for details section
  doc.setFillColor(245, 245, 245);
  doc.rect(12, 47, 176, detailsSectionHeight, 'F');

  let gridY = 56;
  detailsList.forEach((item, index) => {
    const isRight = index % 2 === 1;
    const cellX = isRight ? 110 : 14;
    doc.setFont(fonts.body, 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...grayColor);
    doc.text(`${item.label}`, cellX, gridY);
    doc.setFont(fonts.body, 'bold');
    doc.setTextColor(...textColor);
    const valueLines = doc.splitTextToSize(String(item.value), 76);
    doc.text(valueLines, cellX + 36, gridY);
    if (isRight || index === detailsList.length - 1) {
      gridY += Math.max(6, valueLines.length * 4.5);
    }
  });

  gridY += 6;

  // TABLE
  const columns = [englishT('common.date'), englishT('common.remarks'), englishT('common.debit'), englishT('common.credit'), englishT('common.balance')];
  const dataRows = transactions.map((txn) => [
    fmtDate(txn.transactionDate),
    txn.remarks || '-',
    txn.debitAmount  > 0 ? fmtCurrency(txn.debitAmount)  : '-',
    txn.creditAmount > 0 ? fmtCurrency(txn.creditAmount) : '-',
    fmtCurrency(txn.balance),
  ]);

  autoTable(doc, {
    startY: gridY,
    head: [columns],
    body: dataRows,
    theme: 'striped',
    tableWidth: 170,
    headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9, font: fonts.body, halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: primaryColor },
    bodyStyles: { textColor: textColor, fontSize: 8.5, font: fonts.body, valign: 'middle', minCellHeight: 6, lineWidth: 0.3, lineColor: [220, 220, 220] },
    alternateRowStyles: { fillColor: [255, 250, 247] },
    columnStyles: {
      0: { cellWidth: 26, halign: 'center', lineWidth: 0.3 },
      1: { cellWidth: 60, halign: 'left', lineWidth: 0.3 },
      2: { cellWidth: 28, halign: 'right', lineWidth: 0.3 },
      3: { cellWidth: 28, halign: 'right', lineWidth: 0.3 },
      4: { cellWidth: 28, halign: 'right', lineWidth: 0.3 },
    },
    styles: { font: fonts.body, cellPadding: { top: 2, right: 2, bottom: 2, left: 2 }, overflow: 'linebreak', fontSize: 8.5 },
    margin: { top: 3, right: 14, bottom: 8, left: 14 },
    didDrawPage: ({ pageNumber }) => {
      const totalPages = doc.internal.getNumberOfPages();
      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, 7, 297, 'F');
      doc.setFont(fonts.body, 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(...grayColor);
      const pageMarker = `${englishT('common.page')} ${pageNumber} / ${totalPages}`;
      doc.text(pageMarker, 196 - doc.getTextWidth(pageMarker), 288);
      const notice = englishT('common.computerGenerated');
      doc.text(notice, 14, 288);
    },
  });

  return doc;
};

export const generateSampleStatementPdf = async () => {
  const sampleAccount = { acno: '1234567890', accountName: 'Sample User', displayName: 'Savings', balance: 12345.67 };
  const sampleTx = [{ transactionDate: new Date().toISOString(), remarks: 'Opening balance', debitAmount: 0, creditAmount: 12345.67, balance: 12345.67 }];
  const tenantBrand = { fullName: 'Example Bank', shortName: 'ExBank' };
  const doc = await generateStatementPdf(sampleAccount, sampleTx, tenantBrand, 'last_3months');
  try {
    doc.save('statement-sample.pdf');
  } catch {
    return doc;
  }
};

if (typeof window !== 'undefined' && typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
  try {
    window.generateSampleStatementPdf = generateSampleStatementPdf;
  } catch (e) {
    // ignore
  }
}
